import React from 'react';
import { Container, Row, Col, Card, Badge, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChartLine,
    faExclamationTriangle, faLightbulb, faRocket, faArrowRight,
    faDesktop, faServer, faDatabase, faChartBar, faRandom,
    faBrain, faCheckCircle, faBullseye
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import SEO from '../SEO/SEO';

import pyramid from "../../assets/images/use-cases/AIReadiness-Process.png";
import vis from "../../assets/images/use-cases/AIReadiness-Dashboard.png";

const AIReadinessAssessment = () => {

    return (
        <>
            <SEO
                title="AI Readiness Assessment: AI Digital Twin for Strategy | Nebulyx AI"
                description="Model your organisation as connected processes, data assets, and capabilities. Simulate AI deployment scenarios to find high-value use cases, identify blockers, and build a prioritised roadmap."
                keywords="AI readiness, AI strategy, digital transformation, automation potential, data readiness, AI ROI, enterprise AI"
                canonicalUrl="https://nebulyx.ai/use-cases/ai-readiness-assessment"
                ogType="article"
            />

            <article className="use-case-page">
                {/* Hero */}
                <header className="use-case-hero">
                    <Container>
                        <Row>
                            <Col className={"w-100"}>
                                <div className={"w-100 text-center"}>
                                    <div >
                                        <Badge className="sector-badge m-1 mb-4 h-100">
                                            <FontAwesomeIcon icon={faBrain}/>
                                            &nbsp;
                                            Strategy
                                        </Badge>
                                        <h1 className="use-case-title">Question:&nbsp;Where should we deploy AI first?</h1>
                                    </div>
                                </div>
                                <div>
                                    <p className="use-case-subtitle w-100 text-center">
                                        Model your organisation as connected processes, data assets, and capabilities. Simulate AI deployment scenarios offline, finding high-value use cases and building a prioritised roadmap before committing millions to the wrong pilots.
                                    </p>
                                </div>
                            </Col>
                        </Row>

                        <Row className="hero-stats-inline ">
                            <Col className="stat-item-inline">
                                <FontAwesomeIcon icon={faBullseye} className="stat-icon"/>
                                <span className="stat-value">8</span>
                                <span className="stat-label">Use Cases Assessed</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faCheckCircle} className="stat-icon"/>
                                <span className="stat-value">3</span>
                                <span className="stat-label">Ready to Deploy</span>
                            </Col>
                            <Col  className="stat-item-inline">
                                <FontAwesomeIcon icon={faChartLine} className="stat-icon"/>
                                <span className="stat-value">£8.4M</span>
                                <span className="stat-label">Total Value Identified</span>
                            </Col>
                        </Row>

                    </Container>
                </header>

                {/* Executive Summary */}
                <section className="exec-summary-section">
                    <Container>
                        <h2 className="section-title">Executive Summary</h2>
                        <Row className="g-4">
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span className={"card-icon-wrapper problem me-1"}><FontAwesomeIcon
                                            sty icon={faExclamationTriangle}/></span>The Problem</h3>
                                        <ul className="summary-list">
                                            <li>Vendors pitching solutions without problems</li>
                                            <li>No objective view of where AI will work</li>
                                            <li>Data readiness varies wildly, unknown</li>
                                            <li>85% of AI pilots fail to reach production</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper solution me-1"}><FontAwesomeIcon
                                            sty icon={faLightbulb}/></span>The Solution</h3>
                                        <ul className="summary-list">
                                            <li>Build a digital twin of your organisation</li>
                                            <li>Score every process for AI suitability</li>
                                            <li>Assess data readiness objectively</li>
                                            <li>Simulate deployment strategies before committing</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="summary-card h-100">
                                    <Card.Body>
                                        <h3 className={"text-start"}><span
                                            className={"card-icon-wrapper benefits me-1"}><FontAwesomeIcon
                                            sty icon={faRocket}/></span>The Benefits</h3>
                                        <ul className="summary-list">
                                            <li>Evidence-based prioritisation</li>
                                            <li>Know what to fix before AI can work</li>
                                            <li>Avoid expensive failed pilots</li>
                                            <li>Accelerate time to AI value</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Container>
                </section>

                {/* Process Steps */}
                <section className="content-section process-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin Process</h2>
                        <p className="text-center text-muted mb-4">
                            Connect process, data, and people systems. Score every use case for automation potential and data readiness. Overlay value estimation. Simulate different deployment strategies to build the optimal roadmap.
                        </p>
                        <div className="process-steps-container">
                            <div className="process-step-item">
                                <div className="step-number">1</div>
                                <div className="step-content">
                                    <strong>Connect Data</strong>
                                    <span>Process docs, data catalogue, HR systems, financials</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">2</div>
                                <div className="step-content">
                                    <strong>Score Readiness</strong>
                                    <span>AI suitability, data quality, automation potential</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">3</div>
                                <div className="step-content">
                                    <strong>Quantify Value</strong>
                                    <span>ROI estimation, FTE impact, time to value</span>
                                </div>
                            </div>
                            <div className="step-arrow">
                                <FontAwesomeIcon icon={faArrowRight}/>
                            </div>
                            <div className="process-step-item">
                                <div className="step-number">4</div>
                                <div className="step-content">
                                    <strong>Scenario Analysis</strong>
                                    <span>Test quick wins vs strategic bets vs balanced</span>
                                </div>
                            </div>
                        </div>
                    </Container>
                </section>

                {/* AI Digital Twin - Two Sections */}
                <section className="content-section twin-section">
                    <Container>
                        <h2 className="section-title">The AI Digital Twin</h2>
                        <Row className="g-4">
                            {/* Back-end Architecture */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon backend">
                                                <FontAwesomeIcon icon={faServer}/>
                                            </div>
                                            <h3>Digital Twin Process Flow</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Ingests process documentation with task-level decomposition, data catalogue with quality metrics,
                                            and financial data for value estimation.
                                            ML models score each use case on automation potential, data readiness, and implementation risk-while
                                            roadmap optimisation sequences use cases based on dependencies, quick wins, and foundation-building requirements.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={pyramid} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Front-end UI */}
                            <Col lg={6}>
                                <Card className="twin-view-card h-100">
                                    <Card.Body>
                                        <div className="twin-view-header">
                                            <div className="twin-view-icon frontend">
                                                <FontAwesomeIcon icon={faDesktop}/>
                                            </div>
                                            <h3>AI Readiness Command</h3>
                                        </div>
                                        <p className="twin-view-description">
                                            Visualises the organisation as an AI readiness matrix-plotting use cases by readiness score vs. business value.
                                            Executives can see which departments are AI-ready, identify quick wins in the top-right quadrant,
                                            and simulate different deployment strategies to see how the roadmap changes before committing budget.</p>
                                        <div className="image-parent">
                                            <img className={"image-child-span w-100 h-100"} src={vis} alt=""/>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                        {/* Twin Components */}
                        <Row className="g-4 mt-4">
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faDatabase}/>
                                            </div>
                                            <h4>Data Analysed</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Process Documentation</li>
                                            <li>Data Catalogue & Quality Scores</li>
                                            <li>Task-Level Decomposition</li>
                                            <li>Financial / Cost Data</li>
                                            <li>Skills & Change Readiness</li>
                                            <li>Technology Integration Map</li>
                                            <li>Strategic Priorities</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faChartBar}/>
                                            </div>
                                            <h4>AI / Data Products</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>AI readiness score per use case</li>
                                            <li>Data readiness heatmap</li>
                                            <li>Automation potential ranking</li>
                                            <li>ROI estimation with confidence</li>
                                            <li>Blocker identification</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={4}>
                                <Card className="twin-card h-100">
                                    <Card.Body>
                                        <div className="twin-card-header">
                                            <div className="twin-icon">
                                                <FontAwesomeIcon icon={faRandom}/>
                                            </div>
                                            <h4>Scenarios Tested</h4>
                                        </div>
                                        <ul className="twin-list">
                                            <li>Balanced approach</li>
                                            <li>Quick wins first</li>
                                            <li>Strategic bets (high value)</li>
                                            <li>Customer-facing focus</li>
                                            <li>Cost reduction focus</li>
                                            <li>Foundation-first approach</li>
                                        </ul>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {/* Discovery */}
                        <div className="discovery-highlight mt-5">
                            <Row className="align-items-center">
                                <Col lg={8}>
                                    <h3>The Potential Discovery</h3>
                                    <p>
                                        By modelling the organisation as a digital twin and scoring every use case objectively,
                                        executives could discover that Customer Service is 78% AI-ready while Operations is only 50%, revealing
                                        that the CEO's favourite project would fail while an overlooked chatbot delivers £920k annually.
                                        Simulation may reveal that <strong>starting with 3 quick wins builds organisational muscle and
                                        funds the data foundation</strong> needed for high-value strategic bets 12 months later, a sequenced
                                        roadmap that doubles total ROI compared to going straight for the moonshot.
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <div className="discovery-stat">
                                        <span className="discovery-label">Potential Strategy</span>
                                        <span className="discovery-value">Quick Wins → Fund Foundation → Strategic Bets</span>
                                        <span className="discovery-note">Doubles total ROI vs. moonshot approach</span>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Container>
                </section>

                {/* CTA */}
                <section className="cta-section">
                    <Container>
                        <div className="cta-content">
                            <h2>Ready to Find Your AI Starting Point?</h2>
                            <p>
                                If you're being told "you need AI" but don't know where to start,
                                let's build your AI Readiness Digital Twin together.
                            </p>
                            <div className="cta-buttons">
                                <Button
                                    variant="light"
                                    size="lg"
                                    href="https://cal.eu/jon-cooke/discovery-call"
                                    className="me-3 mb-2"
                                >
                                    Book a Discovery Call
                                    <FontAwesomeIcon icon={faArrowRight} className="ms-2"/>
                                </Button>
                                <Link to="/use-cases">
                                    <Button variant="outline-light" size="lg" className="mb-2">
                                        View More Use Cases
                                    </Button>
                                </Link>
                            </div>
                        </div>
                    </Container>
                </section>
            </article>
        </>
    );
};

export default AIReadinessAssessment;